/**
 * @engine/vfx — VFX preset builders (single-system group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Create a firework VFX graph with preset nodes.
 *
 * Includes:
 * - Burst spawn (50-100 particles)
 * - Random sphere position with small radius
 * - Cone velocity (upward spread)
 * - Gravity and drag
 * - Color over life (orange -> red -> fade)
 * - Size over life (shrink)
 * - Quad output
 */

export function createMagicPortalVFX(
  name: string = "Magic Portal",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "steam_magic_portal",
    name: "Magical Portal",
    nodes: [
      // Ring system
      {
        id: "spawn_ring",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 400 },
        contextId: "ctx_spawn_ring",
      },
      {
        id: "init_ring_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 2, max: 4 },
        contextId: "ctx_init_ring",
      },
      {
        id: "init_ring_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 3,
          radiusVariance: 0.5,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_ring",
      },
      {
        id: "init_ring_velocity",
        type: "vfx.init.velocity_from_center",
        position: { x: 50, y: 250 },
        inputValues: { center: { x: 0, y: 0, z: 0 }, speed: 0.3 },
        contextId: "ctx_init_ring",
      },
      {
        id: "init_ring_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.08, max: 0.2 },
        contextId: "ctx_init_ring",
      },
      {
        id: "init_ring_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 450 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.4, g: 0.2, b: 1, a: 1 } },
              { t: 0.5, color: { r: 0.8, g: 0.3, b: 1, a: 1 } },
              { t: 1, color: { r: 0.3, g: 0.8, b: 1, a: 1 } },
            ],
          },
        },
        contextId: "ctx_init_ring",
      },
      {
        id: "update_ring_vortex",
        type: "vfx.update.vortex",
        position: { x: 50, y: 50 },
        inputValues: {
          center: { x: 0, y: 0, z: 0 },
          axis: { x: 0, y: 0, z: 1 },
          speed: 3,
          pullStrength: -0.5,
        },
        contextId: "ctx_update_ring",
      },
      {
        id: "update_ring_turbulence",
        type: "vfx.update.turbulence",
        position: { x: 50, y: 150 },
        inputValues: {
          intensity: 1,
          frequency: 1.5,
          octaves: 3,
          roughness: 0.5,
        },
        contextId: "ctx_update_ring",
      },
      {
        id: "update_ring_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.1, v: 0.9 },
              { t: 0.8, v: 0.7 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_ring",
      },
      {
        id: "update_ring_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0.3 },
              { t: 0.2, v: 1 },
              { t: 0.8, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_ring",
      },
      {
        id: "output_ring",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_ring",
      },
      // Core system
      {
        id: "spawn_core",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 150 },
        contextId: "ctx_spawn_core",
      },
      {
        id: "init_core_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 1, max: 2 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "sphere",
          radius: 0.8,
          radiusVariance: 0.3,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_velocity",
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 250 },
        inputValues: { speed: 1.5, speedVariance: 1 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.15, max: 0.35 },
        contextId: "ctx_init_core",
      },
      {
        id: "init_core_color",
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 1, g: 1, b: 1, a: 1 } },
        contextId: "ctx_init_core",
      },
      {
        id: "update_core_attract",
        type: "vfx.update.attract",
        position: { x: 50, y: 50 },
        inputValues: {
          target: { x: 0, y: 0, z: 0 },
          strength: 5,
          maxDistance: 3,
          falloff: 1,
        },
        contextId: "ctx_update_core",
      },
      {
        id: "update_core_curl",
        type: "vfx.update.curl_noise",
        position: { x: 50, y: 150 },
        inputValues: { intensity: 3, scale: 0.8, speed: 2 },
        contextId: "ctx_update_core",
      },
      {
        id: "update_core_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.15, v: 1 },
              { t: 0.7, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_core",
      },
      {
        id: "update_core_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 1, g: 1, b: 1, a: 1 } },
              { t: 0.3, color: { r: 0.8, g: 0.5, b: 1, a: 1 } },
              { t: 0.7, color: { r: 0.5, g: 0.3, b: 1, a: 1 } },
              { t: 1, color: { r: 0.3, g: 0.2, b: 0.8, a: 0 } },
            ],
          },
        },
        contextId: "ctx_update_core",
      },
      {
        id: "output_core",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_core",
      },
      // Sparkle system
      {
        id: "spawn_sparkle",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 80 },
        contextId: "ctx_spawn_sparkle",
      },
      {
        id: "init_sparkle_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.3, max: 0.6 },
        contextId: "ctx_init_sparkle",
      },
      {
        id: "init_sparkle_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 150 },
        inputValues: {
          shape: "circle",
          radius: 3.5,
          radiusVariance: 0.8,
          center: { x: 0, y: 0, z: 0 },
        },
        contextId: "ctx_init_sparkle",
      },
      {
        id: "init_sparkle_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 0.02, max: 0.06 },
        contextId: "ctx_init_sparkle",
      },
      {
        id: "init_sparkle_color",
        type: "vfx.init.color",
        position: { x: 50, y: 350 },
        inputValues: { color: { r: 1, g: 1, b: 1, a: 1 } },
        contextId: "ctx_init_sparkle",
      },
      {
        id: "update_sparkle_alpha",
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 50 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.1, v: 1 },
              { t: 0.5, v: 1 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: "ctx_update_sparkle",
      },
      {
        id: "update_sparkle_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 0 },
              { t: 0.2, v: 1.5 },
              { t: 0.4, v: 0.5 },
              { t: 0.6, v: 1.2 },
              { t: 0.8, v: 0.3 },
              { t: 1, v: 0 },
            ],
          },
          scale: 1,
        },
        contextId: "ctx_update_sparkle",
      },
      {
        id: "output_sparkle",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 },
        contextId: "ctx_output_sparkle",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn_ring",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_ring"],
        collapsed: false,
      },
      {
        id: "ctx_init_ring",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_ring_lifetime",
          "init_ring_position",
          "init_ring_velocity",
          "init_ring_size",
          "init_ring_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_ring",
        type: "vfx.update",
        position: { x: 50, y: 750 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "update_ring_vortex",
          "update_ring_turbulence",
          "update_ring_alpha",
          "update_ring_size",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_ring",
        type: "vfx.output",
        position: { x: 50, y: 1200 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_ring"],
        collapsed: false,
      },
      {
        id: "ctx_spawn_core",
        type: "vfx.spawn",
        position: { x: 400, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_core"],
        collapsed: false,
      },
      {
        id: "ctx_init_core",
        type: "vfx.initialize",
        position: { x: 400, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_core_lifetime",
          "init_core_position",
          "init_core_velocity",
          "init_core_size",
          "init_core_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_core",
        type: "vfx.update",
        position: { x: 400, y: 750 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "update_core_attract",
          "update_core_curl",
          "update_core_alpha",
          "update_core_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output_core",
        type: "vfx.output",
        position: { x: 400, y: 1200 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_core"],
        collapsed: false,
      },
      {
        id: "ctx_spawn_sparkle",
        type: "vfx.spawn",
        position: { x: 750, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_sparkle"],
        collapsed: false,
      },
      {
        id: "ctx_init_sparkle",
        type: "vfx.initialize",
        position: { x: 750, y: 200 },
        size: { width: 280, height: 400 },
        nodeIds: [
          "init_sparkle_lifetime",
          "init_sparkle_position",
          "init_sparkle_size",
          "init_sparkle_color",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update_sparkle",
        type: "vfx.update",
        position: { x: 750, y: 650 },
        size: { width: 280, height: 200 },
        nodeIds: ["update_sparkle_alpha", "update_sparkle_size"],
        collapsed: false,
      },
      {
        id: "ctx_output_sparkle",
        type: "vfx.output",
        position: { x: 750, y: 900 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_sparkle"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow_ring_1",
        fromContextId: "ctx_spawn_ring",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_ring",
        toPortId: "spawn_event",
      },
      {
        id: "flow_ring_2",
        fromContextId: "ctx_init_ring",
        fromPortId: "particle",
        toContextId: "ctx_update_ring",
        toPortId: "particle",
      },
      {
        id: "flow_ring_3",
        fromContextId: "ctx_update_ring",
        fromPortId: "particle",
        toContextId: "ctx_output_ring",
        toPortId: "particle",
      },
      {
        id: "flow_core_1",
        fromContextId: "ctx_spawn_core",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_core",
        toPortId: "spawn_event",
      },
      {
        id: "flow_core_2",
        fromContextId: "ctx_init_core",
        fromPortId: "particle",
        toContextId: "ctx_update_core",
        toPortId: "particle",
      },
      {
        id: "flow_core_3",
        fromContextId: "ctx_update_core",
        fromPortId: "particle",
        toContextId: "ctx_output_core",
        toPortId: "particle",
      },
      {
        id: "flow_sparkle_1",
        fromContextId: "ctx_spawn_sparkle",
        fromPortId: "spawn_event",
        toContextId: "ctx_init_sparkle",
        toPortId: "spawn_event",
      },
      {
        id: "flow_sparkle_2",
        fromContextId: "ctx_init_sparkle",
        fromPortId: "particle",
        toContextId: "ctx_update_sparkle",
        toPortId: "particle",
      },
      {
        id: "flow_sparkle_3",
        fromContextId: "ctx_update_sparkle",
        fromPortId: "particle",
        toContextId: "ctx_output_sparkle",
        toPortId: "particle",
      },
    ],
    contextSettings: {
      ctx_init_ring: { capacity: 3000, boundsMode: "auto" },
      ctx_init_core: { capacity: 1000, boundsMode: "auto" },
      ctx_init_sparkle: { capacity: 500, boundsMode: "auto" },
    },
    capacity: { maxParticles: 5000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Phoenix Rising - Fiery bird effect with flames, embers, and wing particles.
 * Multi-system with fire, embers, and wing trails.
 */
